import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';

/** The shape every page component's `pager` object already has. */
export interface PagerState {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  pages: number[];
}

/**
 * The site's pagination control, as one component instead of the same 24 lines
 * of markup repeated across 17 templates in both language trees.
 *
 * Call sites keep their own `setPage` signature, which differs between pages
 * (`setPage(n)` vs `setPage(n, 'page')`):
 *
 *   <raksme-pager [pager]="pager" [pageSize]="pageSize"
 *                 (pageChange)="setPage($event, 'page')"></raksme-pager>
 *
 * Accessibility lives here now rather than in each copy:
 * - the arrows are <a> without href, so they need role/tabindex/keyboard to be
 *   reachable at all - without it the whole pager is mouse-only
 * - labels are chosen by what the control DOES, not by which way its arrow
 *   points: in RTL a left-pointing arrow means "next", so labelling by glyph
 *   would be backwards on every Arabic page
 */
@Component({
  selector: 'raksme-pager',
  templateUrl: './pager.component.html'
})
export class PagerComponent {

  @Input() pager: PagerState;

  /** When set, the pager hides itself unless there is more than one page of items. */
  @Input() pageSize: number = null;

  @Output() pageChange = new EventEmitter<number>();

  constructor(private router: Router) { }

  get isArabic(): boolean {
    return (this.router.url || '').includes('/ar');
  }

  get labels() {
    return this.isArabic
      ? { first: 'الصفحة الأولى', prev: 'الصفحة السابقة', next: 'الصفحة التالية', last: 'الصفحة الأخيرة', nav: 'ترقيم الصفحات' }
      : { first: 'First page', prev: 'Previous page', next: 'Next page', last: 'Last page', nav: 'Page navigation' };
  }

  get visible(): boolean {
    if (!this.pager || !this.pager.pages || !this.pager.pages.length) { return false; }
    if (this.pageSize && this.pager.totalItems != null) {
      return this.pager.totalItems > this.pageSize;
    }
    return true;
  }

  go(page: number, event?: Event): void {
    if (event) { event.preventDefault(); }
    if (!this.pager) { return; }
    if (page < 1 || page > this.pager.totalPages) { return; }
    if (page === this.pager.currentPage) { return; }
    this.pageChange.emit(page);
  }
}
